import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import qs from 'query-string';
import { dataCentralApi } from '@/api/client';
import type { QueryParams, ResponseArray, ResponseObject } from '@/types/shared';

/**
 * Customers — endpoints, envelope shapes and field names are lifted verbatim from
 * the DataCentral v4 backend (`modules/customer`) so the API is hit unchanged.
 * Segment lists share one server-paginated contract; each tab renders a subset of
 * `CustomerListRow`. Detail / counts / create round out the module surface.
 */

/** Base64-encoded JSON `filters` query param — the backend decodes this, raw keys are ignored. */
export const toFilters = (obj: Record<string, unknown>): string =>
  btoa(JSON.stringify(obj));

/* ── Types ─────────────────────────────────────────────────────────────── */

export type CustomerSegment =
  | 'qualified-prospects'
  | 'cool-lead'
  | 'dead-lead'
  | 'active'
  | 'trial'
  | 'cancelled'
  | 'deleted';

/** A named follower (sale/CS) as returned on segment rows. */
export interface CustomerFollowerName {
  employee_name: string;
}

/**
 * Superset of the columns the segment tabs render. Kept loose/optional because
 * each segment returns a different projection (active carries payment/service
 * dates; deleted carries deletion metadata; leads carry followers).
 */
export interface CustomerListRow {
  customer_id: number;
  customer_name: string;
  company_name?: string;
  customer_email?: string;
  customer_phone?: string;
  customer_industry?: string;
  customer_status?: string;
  customer_sale_followers?: CustomerFollowerName[];
  customer_cs_followers?: CustomerFollowerName[];
  customer_sale_follower_names?: CustomerFollowerName[];
  customer_cs_follower_names?: CustomerFollowerName[];
  customer_service_interestings?: string[];
  total_payment?: number;
  first_service_start_date?: string;
  last_service_off_date?: string;
  customer_note?: string;
  customer_created_at?: string;
  customer_updated_at?: string;
  deleted_at?: string;
  deleted_by_name?: string;
  deletion_reason?: string;
}

/** `GET /customers/counts` — segment tab badges. `deleted` present for admins only. */
export interface CustomerCounts {
  qualified_prospects: number;
  cool_leads: number;
  dead_leads: number;
  active: number;
  trial: number;
  cancelled: number;
  deleted?: number;
}

/**
 * `GET /customers/:id` (§3.2). `customer_sale_followers` / `customer_cs_followers`
 * are id arrays; the enriched objects come back on `*_detail`.
 */
export interface CustomerDetail {
  customer_id: number;
  customer_name: string;
  customer_display_name?: string;
  customer_email: string;
  customer_phone: string;
  customer_position?: string;
  customer_type?: string;
  customer_industry?: string;
  customer_website?: string;
  customer_source?: string;
  customer_source_type?: string;
  customer_service_interestings?: string[];
  customer_sale_followers?: number[];
  customer_cs_followers?: number[];
  customer_status: string;
  customer_note?: string;
  customer_dob?: string;
  customer_lead_type?: string;
  customer_company_name?: string;
  customer_country?: string;
  customer_province?: string;
  customer_district?: string;
  customer_address?: string;
  customer_zipcode?: string;
  is_grey_market?: boolean;
  company?: Record<string, unknown>;
  customer_sale_followers_detail?: unknown[];
  customer_cs_followers_detail?: unknown[];
}

/** `POST /customers` payload (§3.1) — three nested sections. */
export interface CustomerCreatePayload {
  customer: {
    customer_name: string;
    customer_email: string;
    customer_phone: string;
    customer_position: string;
    customer_industry: string;
    customer_type?: string;
    customer_website?: string;
    customer_source?: string;
    customer_source_event?: string;
    customer_source_type?: string;
    customer_service_interestings?: string[];
    customer_sale_followers?: number[];
    customer_note?: string;
    customer_lead_type?: string;
    customer_dob?: string;
    referrer_id?: number;
    [key: string]: unknown;
  };
  company?: {
    company_id?: number;
    company_name?: string;
    company_tax?: string;
    company_address?: string;
    company_website?: string;
    company_industry?: string;
    company_establish_date?: string;
    [key: string]: unknown;
  };
  explaination?: {
    explaination_provider?: string;
    explaination_budget?: number;
    explaination_influencer?: string;
    explaination_problem?: string;
    explaination_expected?: string;
    explaination_result?: string;
    explaination_expected_date?: string;
    [key: string]: unknown;
  };
}

/** Result of the email/phone existence checks. */
export interface CustomerExistenceResult {
  exists: boolean;
  exists_in_customers?: boolean;
  exists_in_customer_contacts?: boolean;
  exists_in_contact_sources?: boolean;
}

/* ── Back-compat stub (kept until CustomersPage is replaced) ─────────────── */

export interface Customer {
  customer_id: number;
  customer_name: string;
  customer_email?: string;
  customer_phone?: string;
  customer_type?: string;
  customer_industry?: string;
  customer_status?: string;
  customer_created_at?: string;
}

/** `GET /customers` → `{ results, pagination }`. */
export async function listCustomers(
  params: QueryParams = {},
): Promise<ResponseArray<Customer>> {
  const res = await dataCentralApi.get(`/customers?${qs.stringify(params)}`);
  return res.data;
}

/** `GET /customers/:id` → `{ message, result }`. */
export async function getCustomerById(
  id: number,
): Promise<ResponseObject<Customer>> {
  const res = await dataCentralApi.get(`/customers/${id}`);
  return res.data;
}

/** List customers with TanStack Query (legacy stub page). */
export function useCustomers(params: QueryParams = {}) {
  return useQuery({
    queryKey: customerKeys.list(params),
    queryFn: () => listCustomers(params),
  });
}

/* ── Query keys ────────────────────────────────────────────────────────── */

export const customerKeys = {
  all: ['customers'] as const,
  list: (params: QueryParams) => ['customers', 'list', params] as const,
  counts: () => ['customers', 'counts'] as const,
  segment: (segment: CustomerSegment, params: SegmentParams) =>
    ['customers', 'segment', segment, params] as const,
  detail: (id: number) => ['customers', 'detail', id] as const,
};

/* ── Segment list ──────────────────────────────────────────────────────── */

export interface SegmentParams {
  page?: number;
  page_size?: number;
  q?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  /** Structured filters, base64-encoded into the `filters` query param. */
  filters?: Record<string, unknown>;
}

function segmentQuery(params: SegmentParams): string {
  const { filters, ...rest } = params;
  const q: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(rest)) {
    if (v !== undefined && v !== '') q[k] = v as string | number;
  }
  if (filters && Object.keys(filters).length > 0) q.filters = toFilters(filters);
  return qs.stringify(q);
}

/** `GET /customers/counts` — badge numbers for the segment tabs. */
export function useCustomerCounts(params: SegmentParams = {}) {
  return useQuery({
    queryKey: customerKeys.counts(),
    queryFn: async (): Promise<ResponseObject<CustomerCounts>> => {
      const res = await dataCentralApi.get<ResponseObject<CustomerCounts>>(
        `/customers/counts?${segmentQuery(params)}`,
      );
      return res.data;
    },
  });
}

/** `GET /customers/{segment}` — one server-paginated segment list. */
export function useCustomerSegment(segment: CustomerSegment, params: SegmentParams = {}) {
  return useQuery({
    queryKey: customerKeys.segment(segment, params),
    queryFn: async (): Promise<ResponseArray<CustomerListRow>> => {
      const res = await dataCentralApi.get<ResponseArray<CustomerListRow>>(
        `/customers/${segment}?${segmentQuery({ page: 1, page_size: 20, ...params })}`,
      );
      return res.data;
    },
  });
}

/* ── Detail ────────────────────────────────────────────────────────────── */

/** `GET /customers/:id` — full customer record for the detail page. */
export function useCustomerDetail(id: number) {
  return useQuery({
    queryKey: customerKeys.detail(id),
    enabled: !!id,
    queryFn: async (): Promise<ResponseObject<CustomerDetail>> => {
      const res = await dataCentralApi.get<ResponseObject<CustomerDetail>>(
        `/customers/${id}`,
      );
      return res.data;
    },
  });
}

/* ── Create ────────────────────────────────────────────────────────────── */

/** `POST /customers` — create a customer + company + explaination. */
export function useCreateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: CustomerCreatePayload,
    ): Promise<ResponseObject<CustomerDetail>> => {
      const res = await dataCentralApi.post<ResponseObject<CustomerDetail>>(
        '/customers',
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
}

/* ── Existence checks (wizard step 1) ──────────────────────────────────── */

/** `GET /customers/check-email?email=` → `{ exists, exists_in_* }`. */
export async function checkCustomerEmail(email: string): Promise<CustomerExistenceResult> {
  const res = await dataCentralApi.get<ResponseObject<CustomerExistenceResult>>(
    `/customers/check-email?${qs.stringify({ email })}`,
  );
  return res.data.result;
}

/** `GET /customers/check-phone?phone=` → `{ exists, exists_in_* }`. */
export async function checkCustomerPhone(phone: string): Promise<CustomerExistenceResult> {
  const res = await dataCentralApi.get<ResponseObject<CustomerExistenceResult>>(
    `/customers/check-phone?${qs.stringify({ phone })}`,
  );
  return res.data.result;
}
